/**
 * The invoice recipient's cost centre (#1810, ADR-0012 amendment 2026-10-04).
 *
 * The backend decides what is stored — booking value > unit default >
 * organisation default > none — in `new-backend/admin/src/lib/booking/
 * costCenter.ts` and its Lambda twin. This file only decides what the form
 * PREFILLS when an operator picks an organisation or unit, so the field shows
 * the value the booking will carry. Same precedence, by construction: the
 * form then sends the field explicitly, and the backend stores what it shows.
 */

/** Mirrors the backend limit; the backend refuses anything longer. */
export const COST_CENTER_MAX_LENGTH = 100;

/** The default a booking for this unit / organisation starts with. */
export const defaultCostCenterFor = (
    unitDefault: string | null | undefined,
    orgDefault: string | null | undefined,
): string => unitDefault?.trim() || orgDefault?.trim() || '';

/** Form value → wire value: blank is "none". */
export const costCenterPayload = (value: string): string | null => value.trim() || null;

/**
 * The wire fields for a booking's attribution and cost centre (#1810). Sent
 * whole, every time: the form shows the value the booking will carry.
 */
interface AttributionFormValue {
    organizationId: string | null;
    organizationUnitId: string | null;
    costCenter: string;
}

interface AttributionWire {
    organizationId: string | null;
    organizationUnitId: string | null;
    costCenter: string | null;
}

export const attributionPayload = (attribution: AttributionFormValue): AttributionWire => ({
    organizationId: attribution.organizationId,
    organizationUnitId: attribution.organizationUnitId,
    costCenter: costCenterPayload(attribution.costCenter),
});

/**
 * The attribution fields an EDIT sends: only when the booking's attribution
 * was actually read (`attributionStatus === 'ok'`). Anything else — a failed
 * read, a legacy store, an older backend — sends nothing, which the backend
 * reads as "unchanged"; sending the form's empty seed would wipe a real one.
 */
export const attributionPayloadForEdit = (
    status: string | undefined,
    attribution: AttributionFormValue,
): AttributionWire | Record<string, never> =>
    status === 'ok' ? attributionPayload(attribution) : {};

/** The stored attribution when it was actually read; undefined otherwise. */
export const attributionIfRead = <T>(status: string | undefined, attribution: T): T | undefined =>
    status === 'ok' ? attribution : undefined;
